<script setup lang="ts">
import {useInterviewStore} from "@/stores/interview-store";
import {storeToRefs} from "pinia";
import {onMounted, ref} from "vue";
import {interviewApi, surveyApi} from "@/container";
import CustomerDrugInteractionHeader from "@/drug-interaction/components/header/CustomerDrugInteractionHeader.vue";
import InterviewContainer from "@/interview/components/instance-modal/InterviewContainer.vue";
import InterviewDescription from "@/interview/components/instance-modal/InterviewDescription.vue";
import InterviewSurveyBuilt from "@/interview/components/instance-modal/InterviewSurveyBuilt.vue";
import useInterviewAudio from "@/interview/composables/useInterviewAudio";
import InterviewRecording from "@/interview/components/instance-modal/InterviewRecording.vue";
import InterviewAudioPanel from "@/interview/components/instance-modal/InterviewAudioPanel.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import dayjs from "dayjs";
import {useRoute, useRouter} from "vue-router";
import useSurveyForm from "@/survey/composables/useSurveyForm";

const interviewStore = useInterviewStore();
const router = useRouter();
const route = useRoute();
const {interview, consumerKey, customerId, interviewInstance} = storeToRefs(interviewStore);

const {recordPlayerRef, startAudio, audioStarted, recordFinished, handleImport} = useInterviewAudio();

const loading = ref(false);
const survey = ref();
const submitting = ref(false);

const {initAnswers, submit, handleChange, errors, userAnswers} = useSurveyForm(survey);

onMounted(async () => {
    if (interview.value) {
        loading.value = true;

        try {
            survey.value = await surveyApi().readBuiltSurvey(interview.value.survey.id);
            initAnswers(survey.value);
        } finally {
            loading.value = false;
        }
    }
});

/**
 * Create a new interview instance.
 */
const handleSurveySubmit = async () => {
    if (audioStarted.value) {
        recordPlayerRef.value.finish();
    } else {
        submit(async (answers) => {
            submitting.value = true;

            try {
                await interviewStore.startInterviewInstance();

                if (interviewInstance.value) {
                    const formattedAnswers = Object.keys(answers)
                        .map(key => ({
                            question_id: Number(key),
                            answer: answers[key]
                        }));

                    interviewInstance.value = await interviewApi().updateInstance(interview.value!.id, interviewInstance.value.id, {
                        survey_answers: formattedAnswers,
                        ended_at: dayjs().format('YYYY-MM-DD HH:mm:ss')
                    });

                    interviewStore.updatePatientStore(interviewInstance.value);

                    await navigateToInterviewFinished();
                }
            } finally {
                submitting.value = false;
            }
        });
    }
};

/**
 * Navigate to interview finished page.
 */
const navigateToInterviewFinished = () => {
    return router.replace({name: 'interview-instances.finished', query: {...route.query}});
};
</script>

<template>
  <div v-if="!interview">
    <p>Un problème est survenu lors du chargement de l'entretien.</p>
  </div>
  <div v-else class="flex flex-col w-full gap-4">
    <CustomerDrugInteractionHeader
      v-if="consumerKey"
      class="shadow-xl"
      :consumer-key="consumerKey"
      :customer-id="customerId as number"
      @track-products="$router.push({
        name: 'interview-instances.consumer-product-tracking',
        query: {...$route.query},
      })"
    />
    <div class="flex gap-6 mb-4">
      <InterviewContainer class="flex gap-x-6 flex-1 ml-4 space-y-4">
        <InterviewDescription
          :description="interview.description"
          class="px-5"
          :duration="interview.duration"
          :video-url="interview.video_url"
        />

        <div v-if="loading" class="flex-1">
          <BaseFlexSpinner />
        </div>

        <div v-else-if="survey">
          <InterviewRecording
            ref="recordPlayerRef"
            @start="startAudio"
            @finish="recordFinished"
            @finish-upload="handleImport"
          >
            <h2 class="text-black">
              Questionnaire de l'entretien :
            </h2>
          </InterviewRecording>
          <InterviewAudioPanel />
          <InterviewSurveyBuilt
            density="compact"
            :survey="survey"
            :answers="userAnswers"
            display-collapsible
            :errors="errors"
            @change="handleChange"
          />
          <div class="flex justify-end">
            <BaseButton primary class="my-4" :loading="submitting" @click="handleSurveySubmit">
              Terminer l'entretien
            </BaseButton>
          </div>
        </div>

        <div v-else>
          <p>Impossible de charger le questionnaire de l'entretien.</p>
        </div>
      </InterviewContainer>
      <slot name="shortcuts" />
    </div>
  </div>
</template>

<style scoped>

</style>
